import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api, apiFetch } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { Landmark, Upload, ArrowLeft, Search, X, Check, FileText } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}

export default function ErpBank() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [matchTarget, setMatchTarget] = useState(null);
  const fileRef = useRef(null);

  const loadStatements = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.erpBankStatements(token);
      setStatements(r?.items || []);
    } catch (e) {
      if (e.status === 403) return;
      toast.show(e.message || 'Failed to load', 'error');
    } finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const handleUpload = async (file) => {
    if (!file || !token) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${BASE}/api/erp/bank/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => null);
        throw new Error(err?.detail || 'Upload failed');
      }
      const uploadData = await uploadRes.json();
      const sid = uploadData.statement_id;

      // Auto-match
      try {
        const matchRes = await api.erpBankMatch(token, sid);
        const matched = matchRes?.matched_count ?? 0;
        const unmatched = matchRes?.unmatched_count ?? 0;
        const total = uploadData.entry_count || (matched + unmatched);
        toast.show(`Uploaded ${total} rows · Matched ${matched} · Unmatched ${unmatched}`, 'success');
      } catch {
        toast.show(`Uploaded ${uploadData.entry_count || '?'} rows`, 'success');
      }
      loadStatements();
    } catch (e) {
      toast.show(e.message || 'Upload failed', 'error');
    } finally { setUploading(false); }
  };

  const onFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const loadDetail = async (sid) => {
    setDetailLoading(true);
    try {
      const r = await api.erpBankStatement(token, sid);
      setDetail(r);
    } catch (e) {
      toast.show(e.message || 'Failed to load statement', 'error');
    } finally { setDetailLoading(false); }
  };

  // Detail view
  if (detail) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-bank-detail">
        <div className="flex items-center gap-3">
          <button onClick={() => setDetail(null)} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-white font-bold text-lg">{detail.source_filename}</h2>
            <p className="text-white/40 text-xs">{fmtDate(detail.uploaded_at)} · {detail.entry_count} rows · {detail.matched_count} matched</p>
          </div>
        </div>

        <GlassCard dark className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.04] border-b border-white/10">
                  <th className="px-4 py-3 text-left text-white/50 text-xs font-bold uppercase">Row</th>
                  <th className="px-4 py-3 text-left text-white/50 text-xs font-bold uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-white/50 text-xs font-bold uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-white/50 text-xs font-bold uppercase">Debit ₹</th>
                  <th className="px-4 py-3 text-right text-white/50 text-xs font-bold uppercase">Credit ₹</th>
                  <th className="px-4 py-3 text-right text-white/50 text-xs font-bold uppercase">Balance ₹</th>
                  <th className="px-4 py-3 text-center text-white/50 text-xs font-bold uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-white/50 text-xs font-bold uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {(detail.entries || []).map((e, i) => (
                  <tr key={e.row_num} data-testid={`erp-bank-row-${e.row_num}`} className={`border-b border-white/[0.04] hover:bg-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                    <td className="px-4 py-3 text-white/40 text-xs tabular-nums">{e.row_num}</td>
                    <td className="px-4 py-3 text-white/60 text-xs tabular-nums whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-4 py-3 text-white/80 text-xs max-w-xs truncate">{e.description}</td>
                    <td className="px-4 py-3 text-right text-red-400 text-xs tabular-nums font-bold">{e.debit_rupees > 0 ? formatMoney(e.debit_rupees) : '—'}</td>
                    <td className="px-4 py-3 text-right text-emerald-400 text-xs tabular-nums font-bold">{e.credit_rupees > 0 ? formatMoney(e.credit_rupees) : '—'}</td>
                    <td className="px-4 py-3 text-right text-white text-xs tabular-nums font-bold">{formatMoney(e.balance_rupees)}</td>
                    <td className="px-4 py-3 text-center">
                      {e.matched ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-bold border border-emerald-500/25">
                          Matched · {e.matched_source_type}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-bold border border-amber-500/25">
                          Unmatched
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!e.matched && (
                        <button
                          onClick={() => setMatchTarget(e)}
                          data-testid={`erp-bank-match-btn-${e.row_num}`}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#2563EB] bg-[#2563EB]/10 hover:bg-[#2563EB]/20 transition-colors"
                        >
                          Match to…
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* Manual Match Modal */}
        {matchTarget && (
          <ManualMatchModal
            entry={matchTarget}
            statementId={detail.statement_id}
            token={token}
            formatMoney={formatMoney}
            onClose={() => setMatchTarget(null)}
            onSuccess={() => {
              setMatchTarget(null);
              loadDetail(detail.statement_id);
              loadStatements();
            }}
          />
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-bank-page">
      {/* Upload section */}
      <div
        data-testid="erp-bank-upload"
        onDrop={onFileDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
        className="p-8 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#2563EB]/30 transition-all cursor-pointer text-center"
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
            <span className="text-white/50 text-sm font-medium">Uploading & matching...</span>
          </div>
        ) : (
          <>
            <Upload size={28} className="text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm font-medium">Drop a bank CSV here, or click to select</p>
            <p className="text-white/30 text-[10px] mt-1">Supported: CSV with Date, Description, Debit, Credit, Balance columns</p>
          </>
        )}
        <input
          type="file"
          ref={fileRef}
          accept=".csv"
          className="hidden"
          onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); e.target.value = ''; }}
        />
      </div>

      {/* Statements list */}
      <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider">Uploaded Statements</h3>
      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : statements.length === 0 ? (
        <GlassCard dark className="p-8 text-center">
          <Landmark size={28} className="text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No bank statements uploaded yet</p>
        </GlassCard>
      ) : (
        <div className="space-y-2">
          {statements.map(s => {
            const total = s.entry_count || 0;
            const matched = s.matched_count || 0;
            const pct = total > 0 ? Math.round((matched / total) * 100) : 0;
            return (
              <GlassCard
                dark
                key={s.statement_id}
                className="p-4 cursor-pointer hover:border-white/20 transition-colors"
                onClick={() => loadDetail(s.statement_id)}
                testId={`erp-bank-statement-${s.statement_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 border border-[#2563EB]/20 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-[#2563EB]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{s.source_filename}</p>
                    <p className="text-white/40 text-[10px]">{fmtDate(s.uploaded_at)} · {total} rows</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-24">
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-[#2563EB] transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-white/40 text-[10px] mt-0.5 text-right">{matched}/{total} matched</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {detailLoading && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

/* ── Manual Match Modal ── */
function ManualMatchModal({ entry, statementId, token, formatMoney, onClose, onSuccess }) {
  const toast = useToast();
  const [sourceType, setSourceType] = useState('sale');
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const doSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      let items = [];
      if (sourceType === 'sale') {
        const r = await api.salesList(token, { q: searchQ });
        items = (r?.items || r || []).slice(0, 10);
      } else if (sourceType === 'expense') {
        const r = await api.expensesList(token, { q: searchQ });
        items = (r?.items || r || []).slice(0, 10);
      } else {
        const r = await api.erpJournalList(token);
        items = (r?.items || []).filter(j => j.description.toLowerCase().includes(searchQ.toLowerCase())).slice(0, 10);
      }
      setResults(items);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const matchTo = async (sourceId) => {
    setBusy(true);
    try {
      await api.erpBankManualMatch(token, { statement_id: statementId, row_num: entry.row_num, source_type: sourceType, source_id: sourceId });
      toast.show('Matched successfully', 'success');
      onSuccess();
    } catch (e) {
      toast.show(e.message || 'Match failed', 'error');
    } finally { setBusy(false); }
  };

  const getId = (item) => item.sale_id || item.expense_id || item.entry_id || item.id;
  const getLabel = (item) => item.description || item.invoice_number || item.category || '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-white/10 rounded-3xl max-w-lg w-full mx-4 p-6 shadow-2xl" data-testid="erp-bank-match-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-extrabold text-lg">Match Row #{entry.row_num}</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 text-white/60"><X size={18} /></button>
        </div>
        <p className="text-white/50 text-xs mb-4 truncate">{entry.description} · {formatMoney(entry.debit_rupees || entry.credit_rupees)}</p>

        {/* Source type */}
        <div className="flex gap-1.5 mb-3">
          {['sale', 'expense', 'journal'].map(t => (
            <button
              key={t}
              onClick={() => { setSourceType(t); setResults([]); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${sourceType === t ? 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/25' : 'text-white/40 hover:text-white/60'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Search..."
            className="flex-1 h-10 px-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]/40"
          />
          <button onClick={doSearch} className="px-4 h-10 rounded-xl bg-[#2563EB]/15 text-[#2563EB] font-bold text-xs hover:bg-[#2563EB]/25 transition-colors">
            <Search size={14} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-48 overflow-y-auto space-y-1">
          {searching ? (
            <div className="py-6 flex justify-center">
              <div className="w-5 h-5 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-white/30 text-xs text-center py-4">Search to find matching records</p>
          ) : results.map(r => (
            <button
              key={getId(r)}
              onClick={() => matchTo(getId(r))}
              disabled={busy}
              className="w-full text-left px-3 py-2.5 rounded-lg text-xs text-white/80 hover:bg-white/10 transition-colors flex items-center justify-between gap-2 disabled:opacity-50"
            >
              <span className="truncate">{getLabel(r)}</span>
              <Check size={12} className="text-white/30 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


